<template>
    <StepShell :embedded="embedded" layout="page">
        <StepHeader
            :embedded="embedded"
            :title="t('onboarding.calendarFirst.calendarPayoffStep.title')"
            :lede="t('onboarding.calendarFirst.calendarPayoffStep.lede', { days: windowDays }, windowDays)">
            <template #note>
                <i aria-hidden="true" class="bi bi-calendar3"></i>
                {{ sourceName }}
                <i aria-hidden="true" class="bi bi-lock-fill source-lock"></i>
                {{ t("onboarding.calendarFirst.calendarPayoffStep.readOnly") }}
            </template>
        </StepHeader>

        <div class="told-me">
            <i aria-hidden="true" class="bi bi-stars told-me-glyph"></i>
            <p class="told-me-copy">{{ t("onboarding.calendarFirst.calendarPayoffStep.toldMe") }}</p>
        </div>

        <StatBand v-if="statTiles.length > 0" :tiles="statTiles" />

        <!-- Calendar processing takes long enough that a skeleton alone looks stalled; show explicit
             progress.
             -->
        <section v-if="!readingsSettled" class="waiting" aria-busy="true">
            <div class="waiting-head">
                <span aria-hidden="true" class="waiting-spinner"></span>
                <h2 class="waiting-title">{{ waitingTitle }}</h2>
            </div>

            <p class="waiting-note">{{ waitingNote }}</p>

            <ol class="waiting-stages">
                <li
                    v-for="(stage, index) in waitingStages"
                    :key="stage.label"
                    class="waiting-stage"
                    :class="{ 'is-active': index === waitingStage, 'is-done': index < waitingStage }">
                    <span aria-hidden="true" class="waiting-marker">
                        <i v-if="index < waitingStage" class="bi bi-check-lg" aria-hidden="true"></i>
                    </span>
                    {{ stage.label }}
                </li>
            </ol>

            <output class="waiting-status" :aria-label="t('onboarding.calendarFirst.calendarPayoffStep.scanStatusLabel')">{{ waitingStatus }}</output>
        </section>

        <!-- The three readings are generated in parallel and saved independently, so display each as it
             arrives.
             -->
        <section v-if="cards.length > 0 || pendingSlots > 0" class="readings-block">
            <p class="readings-label">{{ t("onboarding.calendarFirst.calendarPayoffStep.readingsLabel") }}</p>
            <ReadingList :readings="cards" :pending-count="pendingSlots" :show-rating="showRating" :location="location" />
            <!--
                Colleagues shared a profile, not a calendar, and implying
                otherwise would be a privacy claim we cannot back — so "not their
                calendars" has to survive any future trim of this copy.
            -->
            <p v-if="sharerNames.length > 0" class="sharers-copy">{{ sharersCopy }}</p>
        </section>

        <section v-else class="thin">
            <p class="thin-copy">{{ thinCopy }}</p>
        </section>

        <StepActions flush>
            <BaseButton theme="primary" @click="emit('continue')">{{ t("onboarding.calendarFirst.actions.continue") }}</BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import ReadingList from "~vue/CalendarFirstOnboarding/ReadingList.vue";
import StatBand from "~vue/CalendarFirstOnboarding/StatBand.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import type { LabInsightLocation } from "~vue/types/labs";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { statTiles as tilesFor } from "./scanStats";
import StepActions from "./StepActions.vue";
import StepHeader from "./StepHeader.vue";
import StepShell from "./StepShell.vue";
import type { CalendarScanStats, ReadingsState } from "./types";

const {
    embedded = false,
    stats,
    readings,
    readingsSettled,
    showRating = true,
    location = "onboarding",
} = defineProps<{
    embedded?: boolean;
    stats: CalendarScanStats | null;
    readings: ReadingsState | null;
    readingsSettled: boolean;
    /** Offer the rating bar on each reading. Off for the fixture-backed staff gallery. */
    showRating?: boolean;
    /** Which setup entry point is rendering the step. */
    location?: LabInsightLocation;
}>();

const emit = defineEmits<{ continue: [] }>();

const { t } = useI18n();

const READING_SLOT_COUNT = 3;
const MAX_NAMED_SHARERS = 3;

const cards = computed(() => {
    if (readings === null || readings.sparse || readings.status === "failed" || readings.status === "skipped") {
        return [];
    }
    return readings.readings;
});

/* Slots still to be filled. Held open only while more readings can still arrive:
   once the run is settled, what is on the screen is all there is. */
const pendingSlots = computed(() => (readingsSettled ? 0 : Math.max(READING_SLOT_COUNT - cards.value.length, 0)));

/*
 * The three stages are the real shape of the readings pipeline, but it reports no
 * per-stage progress, so they are paced off elapsed time. That is why none of them
 * claims a stage *finished* faster than the whole run can: the last one stays
 * active until the readings themselves land.
 */
const waitingStages = computed(() => [
    { at: 0, label: t("onboarding.calendarFirst.calendarPayoffStep.stages.pulling") },
    { at: 12, label: t("onboarding.calendarFirst.calendarPayoffStep.stages.patterns") },
    { at: 28, label: t("onboarding.calendarFirst.calendarPayoffStep.stages.writing") },
]);

/* Past this the wait is no longer typical, and saying so beats a spinner that
   looks identical at ten seconds and at a minute. */
const SLOW_AFTER_SECONDS = 45;

const elapsedSeconds = ref(0);
let elapsedTimer: ReturnType<typeof setInterval> | null = null;

function stopElapsed(): void {
    if (elapsedTimer !== null) {
        clearInterval(elapsedTimer);
        elapsedTimer = null;
    }
}

watch(
    () => readingsSettled,
    (settled) => {
        if (settled) {
            stopElapsed();
            return;
        }
        if (elapsedTimer === null) {
            elapsedTimer = setInterval(() => {
                elapsedSeconds.value += 1;
            }, 1000);
        }
    },
    { immediate: true },
);

onBeforeUnmount(stopElapsed);

const waitingStage = computed(() => {
    let index = 0;
    waitingStages.value.forEach((stage, position) => {
        if (elapsedSeconds.value >= stage.at) {
            index = position;
        }
    });
    /* A reading on the screen is proof the earlier stages finished, whichever way
       the clock had them paced — the last stage is the one still running. */
    return cards.value.length > 0 ? Math.max(index, waitingStages.value.length - 1) : index;
});

/* Once a card is up, "reading your calendar now" describes work the user can see
   is already done, so the title counts what is left instead. */
const waitingTitle = computed(() => {
    const arrived = cards.value.length;
    if (arrived === 0) {
        return t("onboarding.calendarFirst.calendarPayoffStep.waitingTitle.reading");
    }
    if (pendingSlots.value === 0) {
        return t("onboarding.calendarFirst.calendarPayoffStep.waitingTitle.finishing");
    }
    return t("onboarding.calendarFirst.calendarPayoffStep.waitingTitle.partial", { arrived, pending: pendingSlots.value });
});

/* Named against the scan the user just watched happen, so the wait is visibly
   about their own data rather than a generic "loading". */
const waitingNote = computed(() => {
    if (stats === null) {
        return t("onboarding.calendarFirst.calendarPayoffStep.waitingNote.generic");
    }
    return t("onboarding.calendarFirst.calendarPayoffStep.waitingNote.counted", { meetings: stats.meeting_count, days: stats.window_days });
});

const waitingStatus = computed(() => {
    if (elapsedSeconds.value >= SLOW_AFTER_SECONDS) {
        return t("onboarding.calendarFirst.calendarPayoffStep.waitingStatus.slow");
    }
    if (pendingSlots.value === 0) {
        return t("onboarding.calendarFirst.calendarPayoffStep.waitingStatus.complete");
    }
    if (cards.value.length > 0) {
        return t("onboarding.calendarFirst.calendarPayoffStep.waitingStatus.partial");
    }
    return t("onboarding.calendarFirst.calendarPayoffStep.waitingStatus.typical");
});

const sharerNames = computed(() => (stats?.profile_sharer_names ?? []).slice(0, MAX_NAMED_SHARERS));

const sharerCount = computed(() => stats?.profile_sharer_names.length ?? 0);

/* The second sentence claims the readings drew on those profiles, so it can only be
   said once there are readings on the screen to have drawn on them. */
const sharersCopy = computed(() => {
    const count = sharerCount.value;
    const shared = t("onboarding.calendarFirst.calendarPayoffStep.sharers.shared", { count }, count);
    return cards.value.length > 0 ? `${shared} ${t("onboarding.calendarFirst.calendarPayoffStep.sharers.readAlongside", { count }, count)}` : shared;
});

const sourceName = computed(() => stats?.provider_name || t("onboarding.calendarFirst.calendarPayoffStep.sourceFallback"));
const windowDays = computed(() => stats?.window_days ?? 30);

/*
 * Every tile is a number the scan actually produced. The prototype's "37% of
 * working hours" and "52-minute focus block" are absent because both need a
 * Keep tile definitions in scanStats so the connect screen, loading placeholders, and results agree.
 * Working-hours metrics require a model we do not have.
 */
const statTiles = computed(() => tilesFor(stats));

const thinCopy = computed(() => {
    if (stats !== null && stats.sparse) {
        return t("onboarding.calendarFirst.calendarPayoffStep.thin.sparse", { meetings: stats.meeting_count, days: stats.window_days });
    }
    return t("onboarding.calendarFirst.calendarPayoffStep.thin.failed");
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .step.is-embedded .thin-copy {
        @apply body-small;
    }

    .source-lock {
        @apply ml-1 shrink-0;
    }

    .told-me {
        @apply -mb-1 flex items-center gap-1;
    }

    .told-me-glyph {
        @apply shrink-0 text-base text-ink;
    }

    .told-me-copy {
        @apply nadia-chat text-ink;
    }

    .readings-block {
        @apply flex w-full flex-col gap-2.5;
    }

    .readings-label {
        @apply scaffold-details-small text-ink;
    }

    .sharers-copy {
        @apply scaffold-details-small text-ink-muted;
    }

    .waiting {
        @apply mt-6 flex flex-col items-start border-t border-border pt-4;
    }

    .waiting-head {
        @apply flex items-center gap-2.5;
    }

    .waiting-spinner {
        @apply block size-4 shrink-0 rounded-full border-2 border-border border-t-ink;
        animation: waiting-spin 0.9s linear infinite;
    }

    .waiting-title {
        @apply body-semibold text-ink;
    }

    .waiting-note {
        @apply body-small mt-2.5 text-ink;
    }

    .waiting-stages {
        @apply mt-5 flex flex-col gap-2 text-left;
    }

    .waiting-stage {
        @apply body-small flex items-center gap-2.5 text-ink-muted transition-colors;
    }

    .waiting-stage.is-active {
        @apply body-small-semibold text-ink;
    }

    .waiting-stage.is-done {
        @apply text-ink-muted;
    }

    .waiting-marker {
        @apply details flex size-4 shrink-0 items-center justify-center rounded-full border border-border-strong text-white;
    }

    .waiting-stage.is-active .waiting-marker {
        @apply border-control-accent;
        animation: waiting-pulse 1.6s ease-in-out infinite;
    }

    .waiting-stage.is-done .waiting-marker {
        @apply border-control-accent bg-control-accent;
    }

    .waiting-status {
        @apply details mt-5 block text-ink-muted;
    }

    .thin {
        @apply mt-6 border-y border-border py-6;
    }

    .thin-copy {
        @apply body text-ink;
    }
}

@keyframes waiting-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes waiting-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.4;
    }
}

@media (prefers-reduced-motion: reduce) {
    .waiting-spinner,
    .waiting-stage.is-active .waiting-marker {
        animation: none;
    }
}
</style>
